import React, { useState } from 'react';

import { DateFilterMenuContent } from './date-filter-menu-content';
import { useDateFilterState } from './date-filter-utilities';
import { DATE_ERROR_MESSAGE, FilterOption, DateFilterType, NUMBER_ERROR_MESSAGE } from './date-filter.types';
import { Button } from '../button/button';
import { Popover } from '../popover/popover';
import { QuickFilterButton } from '../quick-filter-button/quick-filter-button';

export interface DateFilterDropdownProps {
  /** The label to display on the chip. e.g. 'Due Date' */
  label: string;
  /** The current filter value */
  value?: DateFilterType;
  /** Callback to trigger when the filter changes */
  onChange: (filter: DateFilterType) => void;
  /** Callback to trigger when the filter is applied */
  onApply: (filter: DateFilterType) => void;
}

// eslint-disable-next-line max-lines-per-function
export const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({ onChange, onApply, value, label }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { filter, reset, apply, updateTempFilter, updateErrors, resetTempFilter } = useDateFilterState(
    onChange,
    onApply,
    value
  );

  const handleApply = () => {
    const newErrors = { ...filter.errors };

    switch (filter.tempFilter.option) {
      case 'between': {
        newErrors.start = filter.tempFilter.startDate === undefined ? DATE_ERROR_MESSAGE : undefined;
        newErrors.end = filter.tempFilter.endDate === undefined ? DATE_ERROR_MESSAGE : undefined;
        newErrors.period = undefined;

        break;
      }
      case 'within last': {
        newErrors.start = undefined;
        newErrors.end = undefined;
        newErrors.period = filter.tempFilter.periodQuantity === undefined ? NUMBER_ERROR_MESSAGE : undefined;

        break;
      }
      case 'after': {
        newErrors.start = filter.tempFilter.startDate === undefined ? DATE_ERROR_MESSAGE : undefined;
        newErrors.end = undefined;
        newErrors.period = undefined;

        break;
      }
      case 'before': {
        newErrors.start = undefined;
        newErrors.end = filter.tempFilter.endDate === undefined ? DATE_ERROR_MESSAGE : undefined;
        newErrors.period = undefined;

        break;
      }
    }
    updateErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== undefined)) {
      setIsDropdownOpen(true);
      return;
    }

    apply();
    setIsDropdownOpen(false);
  };

  const handleDropdownToggle = (isOpen: boolean) => {
    if (isOpen) {
      updateTempFilter({ ...filter.appliedFilter });
    }
    setIsDropdownOpen(isOpen);
  };

  const popoverContent = (
    <>
      <DateFilterMenuContent
        onChange={(filter) => {
          updateTempFilter(filter);
        }}
        onOptionChange={(option: FilterOption | undefined) => {
          // Reset our temp filter first to clear other filter options
          resetTempFilter();
          if (filter.appliedFilter.option === option) {
            // If we are switching back to the applied filter, we want to copy the applied to the
            // temp filter so the user sees their previous choices
            updateTempFilter({ ...filter.appliedFilter });
          } else {
            updateTempFilter({ option: option });
          }
        }}
        dateError={filter.errors}
        dateFilter={filter.tempFilter}
      />
      <div className="flex flex-col">
        <div className="border-gray flex w-full flex-row border-t px-5 py-2.5">
          <div className="flex-1">
            <Button
              type="text"
              onClick={() => {
                reset();
                setIsDropdownOpen(false);
              }}
            >
              Reset
            </Button>
          </div>
          <Button type="primary" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <Popover
      showArrow={false}
      open={isDropdownOpen}
      content={popoverContent}
      onOpenChange={() => handleDropdownToggle(!isDropdownOpen)}
      innerContentClassName="p-0 min-w-64"
    >
      <QuickFilterButton
        label={label}
        icon={undefined}
        isSelected={!!filter.appliedFilter.option}
        onClick={() => handleDropdownToggle(!isDropdownOpen)}
        onClose={(e) => {
          e.stopPropagation();
          reset();
          setIsDropdownOpen(false);
        }}
      />
    </Popover>
  );
};

export default DateFilterDropdown;
